// firmwareUpgrade.js - 固件升级核心逻辑

import { transceive } from './actions';

// 固件配置
export const FIRMWARE_CONFIG = {
  EXPECTED_SIZE: 524288,  // 512KB
  APDU_ENTER_DFU: "00FF222200",
  OFFICIAL_DOWNLOAD_URL: "https://www.griffonoak.com/downloads/firmware",
};

/**
 * 验证固件文件
 * @param {File} file - 固件文件
 * @returns {Object} 验证结果
 */
export async function validateFirmware(file) {
  const errors = [];
  const warnings = [];

  // 1. 检查文件大小
  if (file.size !== FIRMWARE_CONFIG.EXPECTED_SIZE) {
    errors.push(
      `Invalid firmware size: ${file.size} bytes (expected ${FIRMWARE_CONFIG.EXPECTED_SIZE} bytes / 512KB)`
    );
  }

  // 2. 检查文件扩展名
  if (!file.name.endsWith('.bin')) {
    warnings.push('Firmware file should have .bin extension');
  }

  // 3. 检查文件名格式，尝试提取版本号
  const versionMatch = file.name.match(/v?(\d+\.\d+\.\d+)/);
  let detectedVersion = null;
  if (versionMatch) {
    detectedVersion = versionMatch[1];
  } else {
    warnings.push('Could not detect version from filename');
  }

  // 4. 读取固件头部（可选）
  try {
    const header = await readFirmwareHeader(file);
    if (header && header.magic) {
      // 验证魔数（如果固件有特定的头部格式）
      // 这里可以根据实际固件格式添加验证逻辑
    }
  } catch (err) {
    warnings.push(`Could not read firmware header: ${err.message}`);
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    detectedVersion,
    fileSize: file.size,
    fileName: file.name,
  };
}

/**
 * 读取固件头部信息
 * @param {File} file - 固件文件
 * @returns {Object} 头部信息
 */
async function readFirmwareHeader(file) {
  const headerSize = 256;
  const blob = file.slice(0, headerSize);
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);

  // 根据实际固件格式解析
  // 这里返回基本信息，可以根据实际需求扩展
  return {
    magic: bytes.slice(0, 4),
    version: bytes.slice(4, 8),
    // ... 其他字段
  };
}

/**
 * 计算文件 SHA256 校验和
 * @param {File} file - 固件文件
 * @returns {string} SHA256 十六进制字符串
 */
export async function calculateSHA256(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (err) {
    console.error('Failed to calculate SHA256:', err);
    return null;
  }
}

/**
 * 发送 APDU 命令进入 DFU 模式
 * @param {Function} dispatch - Redux dispatch 函数
 * @returns {Object} 操作结果
 */
export async function enterDFUMode(dispatch) {
  try {
    console.log('Sending APDU to enter DFU mode:', FIRMWARE_CONFIG.APDU_ENTER_DFU);

    // 发送 APDU: 00 FF 22 22 00
    // 注意: 设备会立即重启进入 DFU 模式，可能不会返回正常响应
    try {
      const response = await dispatch(transceive(FIRMWARE_CONFIG.APDU_ENTER_DFU));
      console.log('DFU APDU response:', response);
    } catch (err) {
      // 设备重启导致的连接断开是正常的
      console.log('Device disconnected (expected):', err.message);
    }

    return {
      success: true,
      message: 'Device is entering DFU mode',
    };

  } catch (err) {
    console.error('Failed to enter DFU mode:', err);
    return {
      success: false,
      error: err.message,
    };
  }
}

/**
 * 获取当前固件版本
 * @param {Function} dispatch - Redux dispatch 函数
 * @returns {Object} 版本信息
 */
export async function getCurrentFirmwareVersion(dispatch) {
  try {
    // 使用 Admin 认证后的版本查询 APDU
    // 注意: 这个 APDU 需要根据实际设备实现调整
    // 示例: 00 F1 00 00 00
    const apdu = "00F1000000";
    const response = await dispatch(transceive(apdu));

    if (response.endsWith('9000')) {
      const versionHex = response.slice(0, -4);
      const version = parseFirmwareVersionFromHex(versionHex);
      return {
        success: true,
        version,
        raw: versionHex,
      };
    }

    return {
      success: false,
      error: 'Failed to get firmware version',
    };

  } catch (err) {
    return {
      success: false,
      error: err.message,
    };
  }
}

/**
 * 从十六进制字符串解析固件版本
 * @param {string} hex - 十六进制版本字符串
 * @returns {string} 版本号 (如 "1.3.0")
 */
function parseFirmwareVersionFromHex(hex) {
  // 根据实际响应格式解析
  // 示例: 010300 -> 1.3.0
  if (hex.length >= 6) {
    const major = parseInt(hex.slice(0, 2), 16);
    const minor = parseInt(hex.slice(2, 4), 16);
    const patch = parseInt(hex.slice(4, 6), 16);
    return `${major}.${minor}.${patch}`;
  }
  return 'Unknown';
}

/**
 * 比较两个版本号
 * @param {string} current - 当前版本
 * @param {string} target - 目标版本
 * @returns {Object} 比较结果
 */
export function compareVersions(current, target) {
  const parseVersion = (v) => v.split('.').map(Number);

  try {
    const [cMajor, cMinor, cPatch] = parseVersion(current);
    const [tMajor, tMinor, tPatch] = parseVersion(target);

    // 检查是否降级
    if (tMajor < cMajor) return { isNewer: false, isDowngrade: true };
    if (tMajor === cMajor && tMinor < cMinor) return { isNewer: false, isDowngrade: true };
    if (tMajor === cMajor && tMinor === cMinor && tPatch < cPatch) {
      return { isNewer: false, isDowngrade: true };
    }

    // 检查是否相同
    if (tMajor === cMajor && tMinor === cMinor && tPatch === cPatch) {
      return { isNewer: false, isDowngrade: false, isSame: true };
    }

    // 是新版本
    return { isNewer: true, isDowngrade: false };
  } catch (err) {
    return { isNewer: false, error: 'Invalid version format' };
  }
}

/**
 * 生成 DFU 烧录命令
 * @param {string} firmwareFileName - 固件文件名
 * @returns {string} dfu-util 命令
 */
export function generateDFUCommand(firmwareFileName) {
  return `dfu-util -a 0 -s 0x08000000:leave -D ${firmwareFileName}`;
}

/**
 * 格式化文件大小
 * @param {number} bytes - 字节数
 * @returns {string} 格式化后的大小
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
